import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatRelativeTime } from '../lib/time';

const relTime = (iso: string | null): string => {
  if (!iso) return '';
  const norm = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  return formatRelativeTime(norm);
};

const Chip = ({ label, value }: { label: string; value: string | number }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3 py-1 text-[13px] dark:bg-white/[0.06]">
    <span className="font-semibold tabular-nums">{value}</span>
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
  </span>
);

export const RecapCard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = user?.role === 'owner' || user?.role === 'admin';

  const recapQuery = useQuery({
    queryKey: ['recap', 'weekly'],
    queryFn: () => api.recapWeekly(),
    staleTime: 5 * 60_000,
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.recapWeekly(true),
    onSuccess: (data) => {
      queryClient.setQueryData(['recap', 'weekly'], data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(
          data.source === 'fallback' ? 'Dátový súhrn je aktualizovaný.' : 'AI súhrn je aktualizovaný.',
        );
      }
    },
    onError: (error) => toast.error((error as Error).message),
  });

  if (recapQuery.isLoading) {
    return (
      <section className="panel space-y-3 p-5 rise">
        <div className="skeleton h-5 w-40" />
        <div className="skeleton h-14" />
      </section>
    );
  }

  const data = recapQuery.data;
  if (!data) {
    return null;
  }
  const m = data.metrics;
  const busy = refreshMutation.isPending;

  return (
    <section className="panel space-y-4 p-5 rise">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-brand-400/25 to-purple-500/25 text-brand-500 dark:text-brand-300">
            <SparklesIcon className="h-5 w-5" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="t-headline">AI týždenný súhrn</h2>
              {data.source === 'fallback' ? (
                <span className="status-chip status-chip-idle">dátový režim</span>
              ) : data.source === 'ai' ? (
                <span className="status-chip status-chip-live">AI</span>
              ) : null}
            </div>
            {data.generatedAt ? (
              <p className="text-[11px] text-slate-400">aktualizované {relTime(data.generatedAt)}</p>
            ) : (
              <p className="text-[11px] text-slate-400">Cloudflare Workers AI</p>
            )}
          </div>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => refreshMutation.mutate()}
            disabled={busy}
            aria-label="Pregenerovať súhrn"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-black/[0.05] disabled:opacity-50 dark:hover:bg-white/10"
          >
            <ArrowPathIcon className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          </button>
        ) : null}
      </div>

      {data.body ? (
        <p className="text-[15px] leading-relaxed text-slate-700 dark:text-slate-200">{data.body}</p>
      ) : (
        <p className="text-[14px] text-slate-500 dark:text-slate-400">
          {data.error ?? 'Súhrn sa zatiaľ negeneroval.'}
          {canManage ? ' Klikni na obnoviť vpravo hore.' : ''}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Chip label="hodín" value={`${m.hours} h`} />
        <Chip label="commity" value={m.commits} />
        <Chip label="tickety" value={m.ticketsDone} />
        <Chip label="ľudí" value={m.people} />
      </div>
    </section>
  );
};
